import React, { useMemo, useState } from 'react';
import axios from 'axios';
import { isNil } from 'lodash';

import type { LineItemColumnId } from '~/components/AiChat/SideBarDialog/InvoiceRecommendationAnalysis/columnUtils';
import { ConfiguredInvoiceRecommendationAnalysis } from '~/components/AiChat/SideBarDialog/InvoiceRecommendationAnalysis/ConfiguredInvoiceRecommendationAnalysis';
import type {
  InvoiceAnalysisResponse,
  InvoiceData,
  LineItemRow,
} from '~/components/AiChat/SideBarDialog/InvoiceRecommendationAnalysis/types';
import type { ClaimPolymorph } from '~/components/types/claim-types';
import type { InvoiceRecommendation } from '~/components/types/invoice-types';
import SUPPORTED_CURRENCIES from '~/server_shared/generated-types/SUPPORTED_CURRENCIES';
import { reportAxiosError } from '~/Utils';
import { getPolicyCurrency } from '~/Utils/currencyUtils';

import { useClaim } from '../ClaimContainer';
import useFetchExposures from '../exposures/useFetchExposures';
import { useDAConfigsByExposure } from '../hooks/useDAConfigsByExposure';
import { useInvoiceConfig } from '../hooks/useInvoiceConfigs';
import { useInvoice } from '../invoices/hooks/useInvoice';
import { useOdieInvoiceFeatures } from '../invoices/hooks/useOdieInvoiceFeatures';

import { getInvoiceAnalysisConfig } from './invoiceAnalysisConfig';

// Document type matching claim.documents (same as in invoiceAnalysisConfig.ts)
type ClaimDocument = NonNullable<ClaimPolymorph['documents']>[number];

interface GenericInvoiceAnalysisContainerProps {
  /**
   * The document to analyze. We require the full document object (not just documentId)
   * because we depend on its fields (document_name, id) and cannot reliably look it up
   * from claim.documents when opened from notifications (claim data may be stale).
   */
  document: ClaimDocument;
  open: boolean;
  onClose: () => void;
  invoiceRecommendation?: InvoiceRecommendation;
  /**
   * Set by callers that build `invoiceRecommendation` asynchronously (e.g. waiting on
   * claim exposures to load before resolving per-line coverage). While true we keep
   * the skeleton up so the table doesn't initialize from a half-built recommendation.
   */
  isInvoiceRecommendationLoading?: boolean;
}

const GenericInvoiceAnalysisContainer: React.FC<GenericInvoiceAnalysisContainerProps> = ({
  document,
  open,
  onClose,
  invoiceRecommendation,
  isInvoiceRecommendationLoading = false,
}) => {
  const documentId = document.id;
  // Core data hooks - must be called before any conditional returns
  const { claim, onClaimUpdate } = useClaim({ claimRequired: true });
  const { exposures, isExposuresLoading, isExposuresError } = useFetchExposures();
  // Per-row payable_type behavior driven by each exposure's DA config.
  // Strictly opt-in: activates only when at least one DA type has allowed_payable_types
  // set. ODIE mode is exempt (no per-row exposure → entityColumn === 'coverage').
  // useFetchExposures returns Exposure[] | ExposureDisplay[]; both shapes carry
  // `id` and `damage_assessment_type` so the hook's structural shape applies.
  const {
    exposureToDAConfig,
    anyDATypeConfiguresPayable,
    isLoading: isDAConfigsLoading,
  } = useDAConfigsByExposure(exposures as { id: number; damage_assessment_type?: string | null }[] | undefined);
  const {
    invoice: invoiceOrNull,
    isLoading: isInvoiceLoading,
    isError: isInvoiceError,
  } = useInvoice({
    documentId,
  });
  // Convert null to undefined for config functions
  const invoice = invoiceOrNull ?? undefined;
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Invoice config (org-wide) — controls column visibility and labels
  const { invoiceConfig } = useInvoiceConfig(claim.organization_id);

  // ODIE-specific features (grouped for easy removal later)
  const {
    isOdieMode,
    coInsuranceRates,
    coverages,
    isCoverageConfigsLoading,
    isCoverageConfigsError,
    exposuresByCoverageKey,
    handleApplyCoveredItem,
  } = useOdieInvoiceFeatures({
    documentId,
    onClose,
    setIsSubmitting,
  });

  // Get configuration based on mode
  // If saved invoice exists, prefer DB data over recommendation (preserves user edits)
  const hasExistingInvoice = !!invoice;
  const config = useMemo(
    () => getInvoiceAnalysisConfig(invoiceRecommendation, isOdieMode, hasExistingInvoice),
    [invoiceRecommendation, isOdieMode, hasExistingInvoice]
  );

  const payableConfigGuardActive = anyDATypeConfiguresPayable && !isOdieMode;

  // Coverage map for ODIE mode line item mapping
  const coverageMap = useMemo(() => {
    return coverages.reduce(
      (acc, { value, description }) => {
        acc[value] = description;
        return acc;
      },
      {} as Record<string, string>
    );
  }, [coverages]);

  // Exposure entities for non-ODIE mode
  const exposureEntities = useMemo(
    () =>
      (exposures || []).map((exposure) => ({
        value: String(exposure.id),
        description: exposure.label_text || `Exposure #${exposure.claim_internal_id}`,
        damage_assessment_include_depreciation:
          'damage_assessment_include_depreciation' in exposure
            ? (exposure.damage_assessment_include_depreciation as boolean)
            : false,
      })),
    [exposures]
  );

  const policyCurrency = getPolicyCurrency(claim);

  const currencies = SUPPORTED_CURRENCIES.map((currency) => ({
    value: currency,
    label: currency,
  }));

  // Invoice data - derived from config
  const invoiceData: InvoiceData = useMemo(
    () =>
      config.data.getInvoiceData({
        document,
        invoice,
        invoiceRecommendation,
      }),
    [config.data, document, invoice, invoiceRecommendation]
  );

  // Line items - derived from config
  const lineItems = useMemo(
    () =>
      config.data.getLineItems({
        invoice,
        invoiceRecommendation,
        coverageMap,
        policyCurrency,
      }),
    [config.data, invoice, invoiceRecommendation, coverageMap, policyCurrency]
  );

  // Save invoice - includes complete_action_id when invoiceRecommendation exists
  const saveInvoice = async ({ invoice_data, line_items }: InvoiceAnalysisResponse): Promise<boolean> => {
    setIsSubmitting(true);
    try {
      const lineItemsToSave = (line_items || []).map((item) => {
        // Auto-fill payable_type BTS when the row's exposure DA config forces a single
        // value (indemnity/expenses) — the cell is read-only in that case so the user
        // never inputs the value.
        let resolvedPayable: string | null = item.payable_type || null;
        if (payableConfigGuardActive && item.exposure_id) {
          const allowed = exposureToDAConfig.get(item.exposure_id)?.allowed_payable_types;
          if (allowed === 'indemnity' || allowed === 'expenses') resolvedPayable = allowed;
        }
        return {
          ...item,
          exposure_id: item.exposure_id || null,
          coverage_key: config.save.mapLineItemCoverageKey(item),
          // Default payable_type to 'indemnity' for ODIE mode (column not shown).
          payable_type: resolvedPayable || (config.mode === 'odie' ? 'indemnity' : null),
        };
      });

      await axios.post(`/api/v1/claims/${claim.id}/invoice_based_da/save_invoice`, {
        document_id: document.id,
        invoice_id: config.save.getInvoiceId(invoice) || null,
        invoice_number: invoice_data.number || document.document_name || '',
        invoice_date: invoice_data.date,
        vendor: invoice_data.vendor,
        invoice_processing_type: config.save.processingType,
        line_items: lineItemsToSave,
        // Use cliveActionId directly from recommendation (config may not have it when using DB data)
        complete_action_id: invoiceRecommendation?.cliveActionId || null,
      });

      await onClaimUpdate();
      return true;
    } catch (error) {
      await reportAxiosError(error);
      return false;
    } finally {
      setIsSubmitting(false);
    }
  };

  // Validation - mode-specific validation matching original implementations
  const validateInvoice = (lineItems: LineItemRow[]): string | undefined => {
    // Predefined invoice fields (date / currency / description / amount) can be hidden
    // by the org via `predefined_fields.<key>.active = false`. The column-gating logic
    // in ConfiguredInvoiceRecommendationAnalysis honors that flag, so a hidden field
    // must not block save here either. Default to active when the flag is absent.
    const isInvoiceFieldActive = (key: 'item_date' | 'amount_currency' | 'description' | 'amount'): boolean =>
      invoiceConfig?.predefined_fields?.[key]?.active !== false;

    // Standard mode: validate per-line-item date and currency (full editing mode)
    if (config.mode === 'standard') {
      if (isInvoiceFieldActive('item_date')) {
        const itemsWithMissingDate = lineItems.some((item) => !item.item_date || item.item_date.trim() === '');
        if (itemsWithMissingDate) {
          return 'Please provide dates for all line items';
        }
      }

      if (isInvoiceFieldActive('amount_currency')) {
        const itemsWithMissingCurrency = lineItems.some((item) => !item.amount_currency);
        if (itemsWithMissingCurrency) {
          return 'Please provide currency for all line items';
        }
      }
    }

    if (isInvoiceFieldActive('description')) {
      const itemsWithMissingDescription = lineItems.some((item) => !item.description || item.description.trim() === '');
      if (itemsWithMissingDescription) {
        return 'Please provide descriptions for all line items';
      }
    }

    // Standard mode: validate amount
    if (config.mode === 'standard' && isInvoiceFieldActive('amount')) {
      const itemsWithMissingAmount = lineItems.some((item) => isNil(item.amount));
      if (itemsWithMissingAmount) {
        return 'Please provide amounts for all line items';
      }
    }

    const itemsWithMissingCoverageDecision = lineItems.some((item) => !item.coverage_decision);
    if (itemsWithMissingCoverageDecision) {
      return 'Please select coverage decision for all line items';
    }

    // Validate entity selection using config
    const hasInvalidEntity = lineItems.some((item) => !config.validation.validateEntitySelection(item));
    if (hasInvalidEntity) {
      return config.validation.entityValidationMessage;
    }

    // Standard mode: validate payable_type (column is only shown in standard mode).
    // When the per-row payable guard is active, rows whose exposure resolves to a
    // single-value DA config are auto-filled at save time and skip this check.
    if (config.mode === 'standard') {
      const itemsWithMissingPayableType = lineItems.some((item) => {
        if (item.payable_type) return false;
        if (payableConfigGuardActive && item.exposure_id) {
          const allowed = exposureToDAConfig.get(item.exposure_id)?.allowed_payable_types;
          if (allowed === 'indemnity' || allowed === 'expenses') return false;
        }
        return true;
      });
      if (itemsWithMissingPayableType) {
        return 'Please select payable type for all line items';
      }
    }

    return undefined;
  };

  // Status flags
  const isDisabled =
    isSubmitting ||
    isExposuresError ||
    (!invoiceRecommendation && isInvoiceError) ||
    (isOdieMode && isCoverageConfigsError);
  const isLoadingData =
    isExposuresLoading ||
    isInvoiceRecommendationLoading ||
    (!invoiceRecommendation && isInvoiceLoading) ||
    (isOdieMode && isCoverageConfigsLoading) ||
    isDAConfigsLoading;
  const hasError =
    isExposuresError || (!invoiceRecommendation && isInvoiceError) || (isOdieMode && isCoverageConfigsError);

  // Column configuration based on mode — invoice field gating/labels handled by wrapper
  const lineItemColumns = React.useMemo((): LineItemColumnId[] => {
    if (config.mode === 'standard') {
      return [
        'item_date',
        'description',
        'amount_currency',
        'amount',
        'coverage_decision',
        'exposure',
        'payable_type',
        'covered_amount',
        'explanation',
        'actions',
      ];
    }
    // ODIE mode: simplified columns with entity from config
    const cols: LineItemColumnId[] = ['description', 'amount', 'coverage_decision', config.ui.entityColumn];
    cols.push('covered_amount', 'explanation', 'actions');
    return cols;
  }, [config.mode, config.ui.entityColumn]);

  // Title from config
  const customMainTitle = config.ui.getTitle(document.document_name || '');

  return (
    <ConfiguredInvoiceRecommendationAnalysis
      invoiceConfig={invoiceConfig}
      open={open}
      onClose={onClose}
      invoiceData={invoiceData}
      lineItems={lineItems}
      onSave={saveInvoice}
      coInsuranceCoverageRates={coInsuranceRates}
      disabled={isDisabled}
      submitting={isSubmitting}
      isLoading={isLoadingData}
      isError={hasError}
      filePath={`/api/v1/claims/${claim.id}/documents/${documentId}`}
      shouldShowCoverageSummary={config.ui.showCoverageSummary}
      validateBeforeSave={validateInvoice}
      // Entity selection: coverages for ODIE mode, exposures otherwise
      {...(isOdieMode
        ? {
            coverages,
            exposuresByCoverageKey,
          }
        : {
            entities: exposureEntities,
          })}
      possibleCurrencies={currencies}
      customMainTitle={customMainTitle}
      lineItemColumnsToShow={lineItemColumns}
      policyCurrency={policyCurrency}
      claim={claim}
      shouldAutoHandleMultiCurrencyColumns={true}
      shouldShowDatePerLineItem={true}
      shouldShowInvoiceSummary={false}
      onApplyCoveredItem={handleApplyCoveredItem}
      exposureToDAConfig={exposureToDAConfig}
      payableConfigGuardActive={payableConfigGuardActive}
      seedInitialEmptyInvoiceRow
    />
  );
};

export default GenericInvoiceAnalysisContainer;
